import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTests,
  getTestById,
  submitTestResponse,
  getUserTestResults,
} from "../../redux/testSlice";
import { SURVEY_MAPPED_TYPE } from "../../constants/testTypes";
import { useNavigate } from "react-router-dom";
import {
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";

const TestSolvePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const {
    tests,
    currentTest,
    currentTestQuestions,
    userTestResults,
    loading,
    submitResponseSuccess,
  } = useSelector((state) => state.tests);

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showThankYou, setShowThankYou] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);

  useEffect(() => {
    dispatch(getAllTests());
    if (user?._id) {
      dispatch(getUserTestResults(user._id))
        .unwrap()
        .catch(() => navigate("/userHomePage"));
    }
  }, [dispatch, user, navigate]);

  useEffect(() => {
    if (tests.length > 0) {
      // 1. Önce bekleyen onboarding testini ara
      const pendingRegistration = tests.find(
        (test) =>
          test.isActive &&
          test.type === SURVEY_MAPPED_TYPE &&
          !userTestResults.some(
            (result) =>
              result.testId?._id === test._id || result.testId === test._id,
          ),
      );

      // 2. Aktif ve cevaplanmamış onboarding testini kullan
      const activeTest = pendingRegistration;

      if (activeTest && (!currentTest || currentTest._id !== activeTest._id)) {
        dispatch(getTestById(activeTest._id));
      } else if (!activeTest && !loading && tests.length > 0) {
        // Eğer bekleyen anketi yoksa veya hepsi bittiyse ana sayfaya dön
        navigate("/userHomePage");
      }
    }
  }, [tests, userTestResults, dispatch, currentTest, loading, navigate]);

  useEffect(() => {
    if (submitResponseSuccess) {
      setShowThankYou(true);
      const timer = setTimeout(() => {
        navigate("/userHomePage");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitResponseSuccess, navigate]);

  const handleSelectOption = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleNext = () => {
    if (!currentTestQuestions[currentStep]) return;

    const currentQuestionId = currentTestQuestions[currentStep]._id;
    if (!answers[currentQuestionId] && answers[currentQuestionId] !== 0) {
      setOpenWarning(true);
      return;
    }

    if (currentStep + 1 < currentTestQuestions.length) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (!currentTest) return;

    const responseArray = Object.entries(answers).map(
      ([questionId, value]) => ({
        questionId,
        answer: value,
      }),
    );

    dispatch(
      submitTestResponse({
        testId: currentTest._id,
        responses: responseArray,
      }),
    );
  };

  const handleSkipTest = () => {
    navigate("/userHomePage");
  };

  if (loading && !currentTest) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    !currentTest ||
    !currentTestQuestions ||
    currentTestQuestions.length === 0
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h6">No active tests found.</Typography>
      </Box>
    );
  }

  const currentQuestion = currentTestQuestions[currentStep];

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-12 px-4">
      <div className="max-w-[800px] mx-auto bg-white p-8 rounded-[24px] shadow-sm border border-gray-100">
        {showThankYou ? (
          <div className="text-center py-12">
            <h2 className="text-3xl font-bold text-button-bg mb-4">
              Completed!
            </h2>
            <p className="text-gray-600 text-lg">
              Thank you for your responses. Redirecting to your home page...
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex justify-between items-center mb-10">
              <button
                onClick={handleBack}
                disabled={currentStep === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 transition ${currentStep === 0 ? "opacity-30 cursor-not-allowed" : "hover:bg-gray-50"}`}
              >
                <span>&#10094;</span> Back
              </button>
              <button
                onClick={handleSkipTest}
                className="text-gray-400 hover:text-gray-600 transition font-medium"
              >
                Skip Test
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mb-10">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-button-bg">
                  Question {currentStep + 1} of {currentTestQuestions.length}
                </span>
                <span className="text-sm text-gray-400">
                  {Math.round(
                    ((currentStep + 1) / currentTestQuestions.length) * 100,
                  )}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-button-bg h-full transition-all duration-300"
                  style={{
                    width: `${((currentStep + 1) / currentTestQuestions.length) * 100}%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Question Content */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 text-center mb-8 leading-tight">
                {currentQuestion.questionText}
              </h2>

              <div className="space-y-4 max-w-[600px] mx-auto">
                {(currentQuestion.questionType === "MULTIPLE_CHOICE" ||
                  currentQuestion.questionType === "YES_NO") &&
                  currentQuestion.options.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() =>
                        handleSelectOption(currentQuestion._id, opt.value)
                      }
                      className={`w-full p-4 text-left rounded-2xl border-2 transition-all duration-200 font-medium text-lg ${
                        answers[currentQuestion._id] === opt.value
                          ? "bg-button-bg border-button-bg text-white shadow-md transform scale-[1.02]"
                          : "bg-white border-gray-100 text-gray-700 hover:border-button-bg hover:bg-gray-50"
                      }`}
                    >
                      {opt.text}
                    </button>
                  ))}

                {currentQuestion.questionType === "TEXT" && (
                  <textarea
                    value={answers[currentQuestion._id] || ""}
                    onChange={(e) =>
                      handleSelectOption(currentQuestion._id, e.target.value)
                    }
                    className="w-full p-4 border-2 border-gray-100 rounded-2xl focus:border-button-bg focus:ring-0 outline-none transition text-lg"
                    placeholder="Type your answer here..."
                    rows={5}
                  />
                )}

                {currentQuestion.questionType === "SCALE" && (
                  <div className="py-8 px-4">
                    <div className="flex justify-between text-sm font-medium text-gray-500 mb-6">
                      <span className="max-w-[120px]">
                        {currentQuestion.minLabel}
                      </span>
                      <span className="max-w-[120px] text-right">
                        {currentQuestion.maxLabel}
                      </span>
                    </div>
                    <div className="relative h-12 flex items-center">
                      <input
                        type="range"
                        min={currentQuestion.minScale}
                        max={currentQuestion.maxScale}
                        value={
                          answers[currentQuestion._id] ??
                          currentQuestion.minScale
                        }
                        onChange={(e) =>
                          handleSelectOption(
                            currentQuestion._id,
                            parseInt(e.target.value),
                          )
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-button-bg"
                      />
                    </div>
                    <div className="text-center mt-6">
                      <span className="inline-block bg-button-bg text-white px-6 py-2 rounded-full font-bold text-xl">
                        {answers[currentQuestion._id] ??
                          currentQuestion.minScale}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              <button
                onClick={handleNext}
                className="w-full max-w-[400px] py-4 bg-button-bg text-white rounded-2xl font-bold text-xl hover:opacity-90 transition shadow-lg shadow-button-bg/20"
              >
                {currentStep + 1 === currentTestQuestions.length
                  ? "Finish"
                  : "Continue"}
              </button>
            </div>
          </>
        )}
      </div>

      <Snackbar
        open={openWarning}
        autoHideDuration={3000}
        onClose={() => setOpenWarning(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setOpenWarning(false)}
          sx={{ borderRadius: "12px" }}
        >
          Please select an option to continue.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TestSolvePage;
